import React, { useEffect, useMemo, useState } from "react";
import { Card, Empty, PageHeader, Pill, Segmented, Skeleton, Stat, Tag } from "../components/ui";
import { resultsRows, safeGet } from "../lib/api";
import { fmtDate, isNum, money, num, pct } from "../lib/format";

const MARKET_TABS = [
  { value: "US", label: "US" },
  { value: "INDIA", label: "India" },
  { value: "CHINA", label: "China" },
];

function retColor(v) {
  if (!isNum(v)) return "var(--muted)";
  return v >= 0 ? "var(--good)" : "var(--bad)";
}

function historyLabel(h) {
  if (!h || typeof h !== "object") return String(h);
  const parts = [];
  if (h.as_of || h.date) parts.push(fmtDate(h.as_of || h.date, { time: false }));
  if (isNum(h.buys) || Array.isArray(h.buy)) parts.push(`${isNum(h.buys) ? h.buys : h.buy.length} buys`);
  if (isNum(h.sells) || Array.isArray(h.sell)) parts.push(`${isNum(h.sells) ? h.sells : h.sell.length} sells`);
  if (Array.isArray(h.keep)) parts.push(`${h.keep.length} kept`);
  if (h.note || h.reason) parts.push(h.note || h.reason);
  return parts.join(" · ") || JSON.stringify(h);
}

export default function Portfolio() {
  const [market, setMarket] = useState("US");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [rowsByTicker, setRowsByTicker] = useState({});

  useEffect(() => {
    (async () => {
      const res = await safeGet("/api/results", null);
      const map = {};
      resultsRows(res).forEach((r) => {
        map[r.ticker] = r;
      });
      setRowsByTicker(map);
    })();
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    safeGet(`/api/portfolio/${market}`, null).then((p) => {
      if (!alive) return;
      setData((d) => ({ ...d, [market]: p }));
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [market]);

  const p = data[market];
  const holdings = useMemo(() => {
    const list = Array.isArray(p?.holdings) ? p.holdings : [];
    return list.map((h) => {
      const r = rowsByTicker[h.ticker];
      const cur = r?.price;
      const ret = isNum(cur) && isNum(h.entry_price) && h.entry_price ? ((cur - h.entry_price) / h.entry_price) * 100 : null;
      return { ...h, row: r, current: cur, ret, currency: r?.currency };
    });
  }, [p, rowsByTicker]);

  const avgRet = useMemo(() => {
    const xs = holdings.filter((h) => isNum(h.ret));
    return xs.length ? xs.reduce((s, h) => s + h.ret, 0) / xs.length : null;
  }, [holdings]);

  const plan = p?.last_plan || null;
  const history = Array.isArray(p?.history) ? p.history.slice().reverse() : [];
  const targetW = isNum(plan?.target_weight) ? plan.target_weight * 100 : holdings.length ? 100 / holdings.length : null;

  return (
    <>
      <PageHeader title="Portfolio" sub={p?.as_of ? `Equal-weight top picks · as of ${fmtDate(p.as_of, { time: false })}` : "Equal-weight top picks, rebalanced every calendar quarter"}>
        <Segmented options={MARKET_TABS} value={market} onChange={setMarket} label="Market" />
      </PageHeader>

      <div className="stat-grid" style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}>
        <Stat label="Holdings" value={holdings.length} sub={p?.market ? `${p.market} portfolio` : "—"} />
        <Stat label="Target weight" value={isNum(targetW) ? targetW.toFixed(1) : "—"} suffix="%" sub="equal weight per holding" />
        <Stat label="Avg return since entry" value={isNum(avgRet) ? pct(avgRet, { digits: 1 }) : "—"} color={retColor(avgRet)} sub="using latest scan prices" />
        <Stat label="Last plan" value={plan ? (plan.buy?.length || 0) + (plan.sell?.length || 0) : "—"} sub={plan ? `${plan.buy?.length || 0} buy · ${plan.sell?.length || 0} sell · ${plan.keep?.length || 0} keep` : "no rebalance plan yet"} />
      </div>

      <div className="table-wrap">
        <div className="table-scroll">
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 44 }}>#</th>
                <th>Holding</th>
                <th style={{ width: 90 }}>Weight</th>
                <th style={{ width: 110 }}>Entry price</th>
                <th style={{ width: 120 }}>Entry date</th>
                <th style={{ width: 110 }}>Current</th>
                <th style={{ width: 110 }}>Return</th>
                <th style={{ width: 90 }}>Action</th>
                <th style={{ width: 90 }}>Quality</th>
              </tr>
            </thead>
            <tbody>
              {loading && !p && (
                <tr><td colSpan={9}><Skeleton height={14} /></td></tr>
              )}
              {holdings.map((h, i) => (
                <tr key={h.ticker}>
                  <td className="num" style={{ color: "var(--muted)" }}>{i + 1}</td>
                  <td>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span style={{ fontWeight: 600 }}>{h.ticker}</span>
                      <span style={{ fontSize: 11, color: "var(--muted)" }}>{h.row ? `${h.row.name}${h.row.sector ? ` · ${h.row.sector}` : ""}` : "—"}</span>
                    </div>
                  </td>
                  <td className="num">{isNum(h.weight) ? `${(h.weight <= 1 ? h.weight * 100 : h.weight).toFixed(1)}%` : "—"}</td>
                  <td className="num">{money(h.entry_price, h.currency)}</td>
                  <td className="num">{fmtDate(h.entry_date, { time: false })}</td>
                  <td className="num">{money(h.current, h.currency)}</td>
                  <td className="num" style={{ color: retColor(h.ret), fontWeight: 500 }}>{pct(h.ret, { digits: 1 })}</td>
                  <td>{h.row?.action ? <Pill kind={h.row.action.toLowerCase()}>{h.row.action}</Pill> : <span style={{ color: "var(--muted)" }}>—</span>}</td>
                  <td className="num">{num(h.row?.quality_score, 1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && holdings.length === 0 && (
          <Empty title={p ? "No holdings yet" : "Portfolio unavailable"}>
            {p ? "The first rebalance after a scan will fill this portfolio." : "Could not load /api/portfolio — start the backend or run a scan."}
          </Empty>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 3fr) minmax(0, 2fr)", gap: 16 }} className="logs-grid">
        <Card id="p-plan" title="Last rebalance plan" desc={plan ? `${plan.market || market} · ${fmtDate(plan.as_of, { time: false })} · target ${plan.target?.length || 0} names` : "No plan yet"}>
          {plan ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 14 }}>
              <PlanList title="Buy" kind="good" items={(plan.buy || []).map((b) => ({ ticker: b.ticker, reason: b.reason }))} />
              <PlanList title="Sell" kind="bad" items={(plan.sell || []).map((s) => ({ ticker: s.ticker, reason: s.reason }))} />
              <PlanList title="Keep" kind="neutral" items={(plan.keep || []).map((k) => ({ ticker: typeof k === "string" ? k : k.ticker, reason: typeof k === "string" ? "" : k.reason }))} />
            </div>
          ) : (
            <Empty>Rebalance plans appear after the first scan of each quarter.</Empty>
          )}
        </Card>
        <Card id="p-hist" title="History" desc={history.length ? `${history.length} entries` : "No history yet"}>
          {history.length ? (
            <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8, maxHeight: 320, overflow: "auto" }}>
              {history.map((h, i) => (
                <li key={i} style={{ fontSize: 12, display: "flex", gap: 8, alignItems: "flex-start" }}>
                  <span className="dot" style={{ background: "var(--accent)", marginTop: 5 }} />
                  <span className="num" style={{ color: "var(--muted)" }}>{historyLabel(h)}</span>
                </li>
              ))}
            </ol>
          ) : (
            <Empty>Each rebalance is recorded here.</Empty>
          )}
        </Card>
      </div>
    </>
  );
}

function PlanList({ title, kind, items }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Pill kind={kind}>{title}</Pill>
        <span className="num" style={{ fontSize: 12, color: "var(--muted)" }}>{items.length}</span>
      </div>
      {items.length === 0 && <span style={{ fontSize: 12, color: "var(--muted)" }}>none</span>}
      <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
        {items.map((it, i) => (
          <li key={`${it.ticker}-${i}`} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontWeight: 600, fontSize: 13 }}>{it.ticker}</span>
            {it.reason && <Tag kind="neutral" title={it.reason}>{it.reason.length > 48 ? `${it.reason.slice(0, 48)}…` : it.reason}</Tag>}
          </li>
        ))}
      </ul>
    </div>
  );
}
